import { useNavigate } from 'react-router';
import type { AppState } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import { RoutesToSkipRedirectCallbackAuth0 } from '../constants/appRoutes';
import type { ReactNode } from 'react';
import { useAuthOrg } from './useAuthOrg';

const Auth0ProviderWithHistory = ({ children }: { children: ReactNode }) => {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const navigate = useNavigate();
  const { authOrgId } = useAuthOrg();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={domain ?? ''}
      clientId={clientId ?? ''}
      skipRedirectCallback={RoutesToSkipRedirectCallbackAuth0.includes(window.location.pathname)}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
      // Falls back to iframe silent-auth when the SDK has a cached entry that
      // pre-dates `useRefreshTokens` being enabled (no `refresh_token` field on
      // the cache entry), or when the refresh_token grant fails. Without this
      // the SDK throws `MissingRefreshTokenError` and the app crashes with
      // "Something went wrong" until the user manually clears localStorage.
      // See AUTHENTICATION.md §7 (cache migration) for context.
      // Docs: https://auth0.github.io/auth0-spa-js/interfaces/Auth0ClientOptions.html#useRefreshTokensFallback
      useRefreshTokensFallback
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
        ...(authOrgId && { organization: authOrgId }),
      }}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
